import { supabase } from '../lib/supabase'
import type { Show } from '../types/portal'

/**
 * Show writes. Mirrors the relevant parts of utils/projectWrites.ts, one
 * level up: a show has an identity and a shared type/description/contact to
 * insert or update, and projects are assigned to it — nothing else about a
 * show is ever written from the client side.
 */

export interface WriteResult {
  ok: boolean
  error?: string
}

function fail(table: string, error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `${table}: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The database's own show id, recovered by slug for a show created moments ago. */
export async function resolveShowId(showId: string, slug: string): Promise<string | null> {
  if (UUID.test(showId)) return showId
  if (!supabase || !slug) return null

  const { data, error } = await supabase.from('shows').select('id').eq('slug', slug).maybeSingle()
  if (error) {
    fail('shows', error)
    return null
  }
  return data ? String(data.id) : null
}

export interface CreatedShow {
  id: string
  accessToken: string
}

/**
 * Inserts a new show and reads back its generated id and access token. Type,
 * description and client contact are optional — "Turn Into a Show" carries
 * them over from the project becoming the show's first episode, while a show
 * created with nothing yet on it starts blank and is filled in later, from
 * Edit Show.
 */
export async function insertShow(show: {
  client: string
  name: string
  slug: string
  type?: string
  description?: string
  clientContact?: string
}): Promise<{ result: WriteResult; created?: CreatedShow }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase
    .from('shows')
    .insert({
      client_name: show.client,
      show_name: show.name,
      slug: show.slug,
      status: 'active',
      project_type: show.type || null,
      description: show.description || null,
      client_contact: show.clientContact || null,
    })
    .select('id, access_token')
    .single()

  const result = fail('shows', error)
  if (!result.ok || !data) return { result }

  return {
    result: { ok: true },
    created: { id: String(data.id), accessToken: String(data.access_token ?? '') },
  }
}

/** Assigns (or clears, with '') a project's show — the only write this file makes to `projects`. */
export async function assignProjectShow(projectId: string, showId: string): Promise<WriteResult> {
  if (!supabase) return { ok: true }
  const { error } = await supabase
    .from('projects')
    .update({ show_id: showId || null })
    .eq('id', projectId)
  return fail('projects', error)
}

/**
 * Updates a show's own fields — client name, type, description, client
 * contact. These are shared across every episode, so the caller is
 * responsible for mirroring the same patch onto each episode's own copy
 * (PortalContext's `updateShow` does this) — this function only ever writes
 * the `shows` row itself.
 */
export async function updateShowRow(
  showId: string,
  slug: string,
  patch: Partial<Show>,
): Promise<WriteResult> {
  if (!supabase) return { ok: true }

  const realId = await resolveShowId(showId, slug)
  if (!realId) {
    const detail = 'shows: the show could not be found in the database'
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }

  const row: Record<string, unknown> = {}
  if (patch.client !== undefined) row.client_name = patch.client
  if (patch.name !== undefined) row.show_name = patch.name
  if (patch.type !== undefined) row.project_type = patch.type || null
  if (patch.description !== undefined) row.description = patch.description || null
  if (patch.clientContact !== undefined) row.client_contact = patch.clientContact || null
  if (Object.keys(row).length === 0) return { ok: true }

  const { error } = await supabase.from('shows').update(row).eq('id', realId)
  return fail('shows', error)
}
